import {
  ArrowLeftIcon,
  CheckIcon,
  PencilIcon,
  PlusIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import clsx from "clsx";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/UI/button";
import { Checkbox, CheckboxField } from "../components/UI/checkbox";
import { Field, Fieldset, Label } from "../components/UI/fieldset";
import { Heading } from "../components/UI/heading";
import { Input } from "../components/UI/input";
import { Select } from "../components/UI/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/UI/table";
import constants from "../utils/constants";
import { useBottomMenuStore } from "../context";
import {
  Dialog,
  DialogTitle,
  DialogBody,
  DialogActions,
} from "../components/UI/dialog";
import AsyncCreatableSelect from "react-select/async-creatable";
import _ from "lodash";
import { XCircleIcon } from "@heroicons/react/20/solid";

export default function NewEditKeg() {
  const mockKegData = [
    {
      size: "4oz",
      price: "$0.50",
      glassware: "Taster",
      onDigital: true,
      onMobile: false,
      displayName: "4oz",
      actualSize: 4,
      estLoss: "0%",
      margin: "100%",
      markup: "0%",
      cost: "$0.00",
      posModifier: "4oz",
      availableToGo: true,
    },
    {
      size: "16oz",
      price: "$0.50",
      glassware: "Snifter",
      onDigital: false,
      onMobile: true,
      displayName: "16oz",
      actualSize: 16.5,
      estLoss: "-5%",
      margin: "100%",
      markup: "0%",
      cost: "$0.00",
      posModifier: "16oz",
      availableToGo: false,
    },
  ];
  const [producers, setProducers] = useState([
    { label: "Ninkasi", value: "ninkasi" },
    { label: "Caldera", value: "caldera" },
    { label: "Study Break", value: "study_break" },
    { label: "Sand Lily", value: "sand_lily" },
    { label: "Anheuser-Busch", value: "anheuser_busch" },
    { label: "MolsonCoors", value: "molsoncoors" },
  ]);
  const [beverages, setBeverages] = useState([
    { label: "Beautiful Trouble", value: "beautiful_trouble" },
    { label: "Hibiscus Ginger Beer", value: "hibiscus_ginger_beer" },
    { label: "Study Break", value: "study_break" },
    { label: "Willamette Valley Brut", value: "willamette_valley_brut" },
  ]);
  const navigate = useNavigate();
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedProducer, setSelectedProducer] = useState(null);
  const [selectedBeverage, setSelectedBeverage] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newEntry, setNewEntry] = useState("");
  const [isAddingProducer, setIsAddingProducer] = useState(false);
  const [suggestedCorrection, setSuggestedCorrection] = useState(null);
  const [kegCost, setKegCost] = useState(0);
  const { setActiveRow, setSelectedTapNumber } = useBottomMenuStore();

  const knownProducerMistakes = new Map([
    ["budweiser", "Anheuser-Busch"],
    ["millercoors", "MolsonCoors"],
    ["miller coors", "MolsonCoors"],
    ["millercoor", "MolsonCoors"],
    ["miller coor", "MolsonCoors"],
    ["miller", "MolsonCoors"],
    ["coors", "MolsonCoors"],
  ]);

  const findProducerCorrection = (inputValue) => {
    const normalizedInput = _.toLower(_.trim(inputValue));

    return knownProducerMistakes.get(normalizedInput) || null;
  };

  const loadProducers = (inputValue, callback) => {
    setTimeout(() => {
      const filteredOptions = _.filter(producers, (p) =>
        _.includes(_.toLower(p.label), _.toLower(inputValue))
      );
      callback(filteredOptions);
    }, 500);
  };

  const loadBeverages = (inputValue, callback) => {
    setTimeout(() => {
      const filteredOptions = _.filter(beverages, (b) =>
        _.includes(_.toLower(b.label), _.toLower(inputValue))
      );
      callback(filteredOptions);
    }, 500);
  };

  const handleCreate = (inputValue, type) => {
    if (type === "beverage") {
      navigate(constants.ROUTE_PATHS.EDIT_BEVERAGE, {
        state: { beverageName: inputValue },
      });
    } else {
      const correctedProducer = findProducerCorrection(inputValue);

      setNewEntry(inputValue); // Keep user's input
      setIsAddingProducer(true);
      setIsDialogOpen(true);

      if (correctedProducer) {
        setSuggestedCorrection(correctedProducer);
      } else {
        setSuggestedCorrection(null);
      }
    }
  };

  const confirmNewEntry = () => {
    const newOption = {
      label: newEntry,
      value: newEntry.toLowerCase().replace(/\s/g, "_"),
    };

    if (isAddingProducer) {
      setProducers((prevProducers) => [...prevProducers, newOption]);
      setSelectedProducer(newOption);
    } else {
      setBeverages((prevBeverages) => [...prevBeverages, newOption]);
      setSelectedBeverage(newOption);
    }

    setIsDialogOpen(false);
  };

  const formatCurrency = (value) => {
    const numberValue = parseFloat(value.replace(/[^0-9.]/g, ""));
    if (isNaN(numberValue)) {
      return "";
    }
    return numberValue.toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
    });
  };

  return (
    <div className="p-3 bg-gray-50 h-full">
      <div className="flex flex-row gap-2 mb-2">
        <Button plain>
          <ArrowLeftIcon
            onClick={() => {
              setActiveRow(null);
              setSelectedTapNumber(null);
              navigate(-1);
            }}
          />
        </Button>
        <Heading> New/Edit Keg</Heading>
        <div className="ml-auto flex-row flex gap-2 text-nowrap mb-1">
          <Select name="Reports" className="w-auto" defaultValue="reports">
            <option value="reports" disabled>
              Reports
            </option>
            <option value="stats">Keg Statistics</option>
            <option value="perf">Historical Performance</option>
            <option value="lvl">Historical Level Accuracy</option>
          </Select>
          <Button>Replacement Restrictions</Button>
          <Button>Add More to On Deck</Button>
          <Button>Change Tap</Button>
          <Button>Replace with Same</Button>
        </div>
      </div>

      {/* Inputs Before Table */}
      <Fieldset className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        {/* Producer */}
        <Field className={"flex flex-row gap-2 items-baseline"}>
          <div>
            <Label required>Producer:</Label>
          </div>
          <AsyncCreatableSelect
            cacheOptions
            defaultOptions={producers}
            loadOptions={loadProducers}
            onCreateOption={(inputValue) =>
              handleCreate(inputValue, "producer")
            }
            onChange={setSelectedProducer}
            value={selectedProducer}
            className="flex-1"
            formatCreateLabel={(userInput) => `Add "${userInput}"`}
          />
          <div className="flex flex-row">
            <Button outline>
              <PlusIcon />
            </Button>
            <Button outline>
              <PencilIcon />
            </Button>
          </div>
        </Field>

        {/* Category */}
        <Field>
          <Select name="category" defaultValue="contact" disabled={true}>
            <option value="contact" disabled>
              Contact us about using categories!
            </option>
            <option value="draft">Draft</option>
            <option value="bottle">Bottle</option>
            <option value="can">Can</option>
          </Select>
        </Field>

        {/* Beverage */}
        <Field className={"flex flex-row gap-2 items-baseline"}>
          <div>
            <Label required>Beverage:</Label>
          </div>
          <AsyncCreatableSelect
            cacheOptions
            defaultOptions={beverages}
            loadOptions={loadBeverages}
            onCreateOption={(inputValue) =>
              handleCreate(inputValue, "beverage")
            }
            onChange={setSelectedBeverage}
            value={selectedBeverage}
            className="flex-1"
            formatCreateLabel={(userInput) => `Add "${userInput}"`}
          />
          <div className="flex flex-row">
            <Button outline>
              <PlusIcon
                onClick={() => navigate(constants.ROUTE_PATHS.EDIT_BEVERAGE)}
              />
            </Button>
            <Button outline>
              <PencilIcon
                onClick={() => navigate(constants.ROUTE_PATHS.EDIT_BEVERAGE)}
              />
            </Button>
          </div>
        </Field>

        {/* Beverage Type */}
        <Field className={"flex flex-row gap-2 items-baseline"}>
          <div>
            <Label className={"whitespace-nowrap"}>Beverage Type:</Label>
          </div>
          <Select name="beverageType" defaultValue="">
            <option value="" disabled>
              Select Beverage Type
            </option>
            <option value="ale">Ale</option>
            <option value="lager">Lager</option>
          </Select>
        </Field>

        {/* Keg Size */}
        <Field className={"flex flex-row gap-2 items-baseline"}>
          <div>
            <Label className={"whitespace-nowrap"}>Keg Size:</Label>
          </div>
          <Select name="kegSize" defaultValue="">
            <option value="" disabled>
              Keg Size
            </option>
            <option value="ale">Half</option>
            <option value="lager">Quarter</option>
          </Select>
          <Button outline className="text-nowrap">
            Size Chart
          </Button>
        </Field>

        {/* Keg Deposit */}
        <Field className="flex flex-row gap-2 items-baseline">
          <div>
            <Label className="whitespace-nowrap">Keg Deposit:</Label>
          </div>
          <Input type="number" placeholder="$.0.00" />
        </Field>

        {/* Year */}
        <Field className={"flex flex-row items-baseline gap-2"}>
          <div>
            <Label>Year:</Label>
          </div>
          <Input type="date" />
        </Field>

        <CheckboxField>
          <Checkbox name="showVintage" />
          <Label>Show Vintage</Label>
        </CheckboxField>
      </Fieldset>

      <Fieldset className="grid grid-cols-2 sm:grid-cols-4 gap-2 my-3 items-center">
        {/* Keg Cost */}
        <Field className={"flex flex-row gap-2 items-baseline"}>
          <div>
            <Label className={"whitespace-nowrap"}>Keg Cost:</Label>
          </div>
          <Input
            type="text"
            placeholder="$0.00"
            value={kegCost}
            onBlur={() => setKegCost(formatCurrency(kegCost))}
            onChange={(e) => {
              const val = e.target.value;
              setKegCost(val);
            }}
          />
        </Field>
        {kegCost ? (
          <Field className="flex flex-row justify-start gap-3 my-2">
            <Button outline>Calculate Pricing</Button>
            {/* {selectedRow != null && <Button outline>Remove Size</Button>} */}
          </Field>
        ) : null}
      </Fieldset>

      {/* Table */}
      <Table
        dense
        className="[--gutter:--spacing(8)] sm:[--gutter:--spacing(8)] mt-2 my-2"
      >
        <TableHead>
          <TableRow>
            <TableHeader></TableHeader>
            <TableHeader>Oz</TableHeader>
            <TableHeader>Price</TableHeader>
            <TableHeader>Glassware</TableHeader>
            <TableHeader>On Digital</TableHeader>
            <TableHeader>On Mobile</TableHeader>
            <TableHeader>Display Name</TableHeader>
            <TableHeader>Actual Size</TableHeader>
            <TableHeader>Est. Loss</TableHeader>
            <TableHeader>Margin</TableHeader>
            <TableHeader>Markup</TableHeader>
            <TableHeader>Cost</TableHeader>
            <TableHeader>POS Modifier</TableHeader>
            <TableHeader>Available To Go</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {mockKegData.map((row, index) => (
            <TableRow
              key={index}
              // onClick={() => setSelectedRow(selectedRow === index ? null : index)}
              className={clsx(
                "cursor-default transition-colors duration-200",
                selectedRow === index ? "bg-orange-200" : "hover:bg-gray-100"
              )}
            >
              <TableCell
                className={clsx("flex justify-center items-center gap-2")}
              >
                <XCircleIcon className="w-6 h-6 text-red-600 hover:text-red-800 cursor-pointer" />
              </TableCell>
              <TableCell>{row.size}</TableCell>
              <TableCell>{row.price}</TableCell>
              <TableCell>{row.glassware}</TableCell>
              <TableCell>
                {row.onDigital ? (
                  <CheckIcon className="w-4 h-4 text-green-500" />
                ) : (
                  <XMarkIcon className="w-4 h-4 text-red-500" />
                )}
              </TableCell>
              <TableCell>
                {row.onMobile ? (
                  <CheckIcon className="w-4 h-4 text-green-500" />
                ) : (
                  <XMarkIcon className="w-4 h-4 text-red-500" />
                )}
              </TableCell>
              <TableCell>{row.displayName}</TableCell>
              <TableCell>{row.actualSize}</TableCell>
              <TableCell>{row.estLoss}</TableCell>
              <TableCell>{row.margin}</TableCell>
              <TableCell>{row.markup}</TableCell>
              <TableCell>{row.cost}</TableCell>
              <TableCell>{row.posModifier}</TableCell>
              <TableCell>
                {row.availableToGo ? (
                  <CheckIcon className="w-4 h-4 text-green-500" />
                ) : (
                  <XMarkIcon className="w-4 h-4 text-red-500" />
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Inputs After Table */}
      <Fieldset className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Field className={"flex flex-row items-baseline gap-2"}>
          <div>
            <Label className={"text-nowrap"}>Serve After:</Label>
          </div>
          <Input type="date" className="flex-1" />
        </Field>
        <Field className={"flex flex-row items-baseline gap-2"}>
          <div>
            <Label required className={"whitespace-nowrap"}>Storage Location:</Label>
          </div>
          <Select>
            <option value="" disabled>
              Stored At
            </option>
            <option>Cooler</option>
            <option>Cellar</option>
          </Select>
        </Field>
        <Field className={"flex flex-row items-baseline gap-2"}>
          <div>
            <Label className={"whitespace-nowrap"}>Badge Icon:</Label>
          </div>
          <Select>
            <option>Select Badge Icon</option>
          </Select>
        </Field>
        <Field className={"flex flex-row items-baseline gap-2"}>
          <div>
            <Label className={"whitespace-nowrap"}>Custom Icon:</Label>
          </div>
          <Select>
            <option>Select Custom Icon</option>
          </Select>
        </Field>
        <Field className={"flex flex-row items-baseline gap-2"}>
          <div>
            <Label className={"whitespace-nowrap"}>Notes:</Label>
          </div>
          <Input placeholder="Notes" />
        </Field>
        <CheckboxField>
          <Checkbox name="doNotTap" />
          <Label>Do Not Tap</Label>
        </CheckboxField>
      </Fieldset>

      <div className="flex flex-row justify-end gap-3 mt-2">
        <Button>Save</Button>
        <Button
          onClick={() => {
            setActiveRow(null);
            setSelectedTapNumber(null);
            navigate(-1);
          }}
        >
          Cancel
        </Button>
      </div>

      {/* Dialog for Adding New Producer or Beverage */}
      <Dialog open={isDialogOpen} onClose={() => setIsDialogOpen(false)}>
        <DialogTitle>
          Add New {isAddingProducer ? "Producer" : "Beverage"}
        </DialogTitle>
        <DialogBody>
          {suggestedCorrection && (
            <div className="bg-yellow-100 text-yellow-800 p-2 rounded mb-2">
              <strong>Suggestion:</strong> "{newEntry}" is often confused with
              <strong> {suggestedCorrection}.</strong> Do you want to select
              <strong> {suggestedCorrection}</strong> instead?
            </div>
          )}
          <Field>
            <Label>Name:</Label>
            <Input
              type="text"
              value={newEntry}
              onChange={(e) => setNewEntry(e.target.value)}
            />
          </Field>
        </DialogBody>
        <DialogActions>
          <Button outline onClick={() => setIsDialogOpen(false)}>
            Cancel
          </Button>
          {suggestedCorrection && (
            <Button
              outline
              onClick={() => {
                setSelectedProducer({
                  label: suggestedCorrection,
                  value: _.snakeCase(suggestedCorrection),
                });
                setIsDialogOpen(false);
              }}
            >
              Use {suggestedCorrection}
            </Button>
          )}
          <Button onClick={confirmNewEntry}>Add</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
